import { storage } from "@/lib/storage";

/**
 * The dashboard setting that lives on the device, not the server.
 *
 * Extracted from the dashboard screen so the prefetch can read it without
 * importing a 2,500-line component — and so both agree on what `resetAt` means
 * rather than parsing the same blob two ways.
 *
 * `resetAt` is load-bearing beyond display: it is the floor every metric is
 * counted from, so it is part of the dashboard cache key. A payload read before
 * a reset describes a world that no longer exists.
 *
 * A `metricsOff` switch used to live here too. It hid the summary and the
 * trend chart while leaving every number one tap away behind the same tiles,
 * so it promised a privacy it did not deliver and cost a seven-query read on
 * every open to render nothing. Removed rather than repaired. Anything still
 * stored under the old key is ignored by the parse below.
 */
export type DashboardControls = {
  /** ISO timestamp of the last "reset my metrics", or null. */
  resetAt: string | null;
};

export const DASHBOARD_CONTROLS_KEY = "opncue.dashboardControls";

export const DEFAULT_DASHBOARD_CONTROLS: DashboardControls = {
  resetAt: null,
};

/** Tolerant by design: unreadable settings mean the defaults, never a crash on
 *  a screen the user was only trying to open. */
export function parseDashboardControls(value: string | null): DashboardControls {
  if (!value) return DEFAULT_DASHBOARD_CONTROLS;
  try {
    const parsed = JSON.parse(value) as Partial<DashboardControls>;
    const resetAt =
      typeof parsed.resetAt === "string" &&
      Number.isFinite(new Date(parsed.resetAt).getTime())
        ? parsed.resetAt
        : null;
    return { resetAt };
  } catch {
    return DEFAULT_DASHBOARD_CONTROLS;
  }
}

export async function readDashboardControls(): Promise<DashboardControls> {
  return parseDashboardControls(await storage.get(DASHBOARD_CONTROLS_KEY));
}

export async function persistDashboardControls(
  value: DashboardControls,
): Promise<void> {
  await storage.set(DASHBOARD_CONTROLS_KEY, JSON.stringify(value));
}
